import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import { DarkModeContext } from "../../context/DarkModeContext";
import { getToken } from "../../utils/auth";
import { Icono } from "./iconos";
import { defaultFilters, fetchCampaignOptions, fetchDashboard } from "./dashboardApi";
import {
    ZMBadge,
    ZMCard,
    ZMChartBox,
    ZMEmptyState,
    ZMErrorState,
    ZMFiltersBar,
    ZMLayout,
    ZMLoadingState,
    ZMMetricCard,
    ZMTable,
    buildZmChartOptions,
} from "./components/ZMUI";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const fmt = (n, dec = 0) =>
    n == null ? "-" : Number(n).toLocaleString("es-AR", { minimumFractionDigits: dec, maximumFractionDigits: dec });
const fmtUsd = (n) => (n == null ? "-" : `US$ ${fmt(n, 2)}`);
const fmtPct = (n) => (n == null ? "-" : `${fmt(n, 2)}%`);

const normalize = (value) =>
    String(value || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim();

const genderLabel = (value) => {
    const key = normalize(value);
    if (key === "female" || key === "mujer" || key === "mujeres") return "Mujeres";
    if (key === "male" || key === "hombre" || key === "hombres") return "Hombres";
    if (key === "unknown" || key === "sin dato" || !key) return "Sin dato";
    return value || "Sin dato";
};

const defaultState = {
    filas: [],
    meta: {},
    campaignOptions: [],
    kpis: {},
};

export default function ZMAudiencias() {
    const { darkMode } = useContext(DarkModeContext);
    const navigate = useNavigate();
    const [cargando, setCargando] = useState(true);
    const [error, setError] = useState("");
    const [datos, setDatos] = useState(defaultState);
    const [campaignOptions, setCampaignOptions] = useState([]);
    const [filters, setFilters] = useState(defaultFilters());
    const [draftFilters, setDraftFilters] = useState(defaultFilters());

    const cargar = async (nextFilters = filters) => {
        const token = getToken();
        if (!token) {
            navigate("/");
            return;
        }

        setCargando(true);
        setError("");
        try {
            const [audienciasPayload, optionItems] = await Promise.all([
                fetchDashboard("audiencias", token, nextFilters),
                fetchCampaignOptions(token, nextFilters),
            ]);

            setDatos(audienciasPayload || defaultState);
            setCampaignOptions(optionItems);
        } catch (e) {
            if (e?.code === 401 || e?.code === 403 || e?.message === "unauthorized") {
                navigate("/");
                return;
            }
            setError("No se pudo cargar el análisis de audiencias.");
        } finally {
            setCargando(false);
        }
    };

    useEffect(() => {
        cargar(filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate]);

    const filas = useMemo(() => datos?.filas || [], [datos]);
    const kpis = datos?.kpis || {};
    const ageGenderRows = useMemo(() => filas.filter((f) => f.breakdown === "age,gender"), [filas]);
    const countryRows = useMemo(() => filas.filter((f) => f.breakdown === "country"), [filas]);
    const regionRows = useMemo(() => filas.filter((f) => f.breakdown === "region"), [filas]);

    const totalLeadsMeta = useMemo(
        () => filas.reduce((sum, row) => sum + (row.leadsMetaReported ?? row.leads ?? 0), 0),
        [filas]
    );
    const totalReach = useMemo(() => filas.reduce((sum, row) => sum + (row.alcance || 0), 0), [filas]);
    const totalImpressions = useMemo(() => filas.reduce((sum, row) => sum + (row.impresiones || 0), 0), [filas]);
    const spendProxy = useMemo(
        () => filas.reduce((sum, row) => sum + ((row.cpl || 0) * (row.leadsMetaReported ?? row.leads ?? 0)), 0),
        [filas]
    );
    const totalClicksProxy = useMemo(() => filas.reduce((sum, row) => {
        const ctr = Number(row.ctr || 0);
        const impressions = Number(row.impresiones || 0);
        return sum + ((impressions * ctr) / 100);
    }, 0), [filas]);
    const cplPromedio = totalLeadsMeta > 0 ? spendProxy / totalLeadsMeta : null;
    const ctrPromedio = totalImpressions > 0 ? (totalClicksProxy / totalImpressions) * 100 : null;

    const porEdad = useMemo(() => {
        const map = new Map();
        ageGenderRows.forEach((row) => {
            const age = row.age || "Sin dato";
            const current = map.get(age) || { age, leads: 0, alcance: 0, impresiones: 0, spend: 0, clicks: 0 };
            const leadsMeta = row.leadsMetaReported ?? row.leads ?? 0;
            current.leads += leadsMeta;
            current.alcance += row.alcance || 0;
            current.impresiones += row.impresiones || 0;
            current.spend += (row.cpl || 0) * leadsMeta;
            current.clicks += ((row.impresiones || 0) * (row.ctr || 0)) / 100;
            map.set(age, current);
        });

        return Array.from(map.values())
            .map((item) => ({
                ...item,
                cpl: item.leads > 0 ? item.spend / item.leads : null,
                ctr: item.impresiones > 0 ? (item.clicks / item.impresiones) * 100 : null,
            }))
            .sort((a, b) => (b.leads || 0) - (a.leads || 0));
    }, [ageGenderRows]);

    const porGenero = useMemo(() => {
        const map = new Map();
        ageGenderRows.forEach((row) => {
            const gender = genderLabel(row.gender);
            const current = map.get(gender) || { gender, leads: 0, alcance: 0, impresiones: 0, spend: 0, clicks: 0 };
            const leadsMeta = row.leadsMetaReported ?? row.leads ?? 0;
            current.leads += leadsMeta;
            current.alcance += row.alcance || 0;
            current.impresiones += row.impresiones || 0;
            current.spend += (row.cpl || 0) * leadsMeta;
            current.clicks += ((row.impresiones || 0) * (row.ctr || 0)) / 100;
            map.set(gender, current);
        });

        return Array.from(map.values())
            .map((item) => ({
                ...item,
                cpl: item.leads > 0 ? item.spend / item.leads : null,
                ctr: item.impresiones > 0 ? (item.clicks / item.impresiones) * 100 : null,
                share: totalLeadsMeta > 0 ? (item.leads / totalLeadsMeta) * 100 : 0,
            }))
            .sort((a, b) => (b.leads || 0) - (a.leads || 0));
    }, [ageGenderRows, totalLeadsMeta]);

    const geos = useMemo(() => {
        const source = countryRows.length > 0 ? countryRows : regionRows;
        const keyField = countryRows.length > 0 ? "country" : "region";
        const map = new Map();

        source.forEach((row) => {
            const key = row[keyField] || "Sin dato";
            const current = map.get(key) || { nombre: key, leads: 0, alcance: 0, impresiones: 0, spend: 0, clicks: 0 };
            const leadsMeta = row.leadsMetaReported ?? row.leads ?? 0;
            current.leads += leadsMeta;
            current.alcance += row.alcance || 0;
            current.impresiones += row.impresiones || 0;
            current.spend += (row.cpl || 0) * leadsMeta;
            current.clicks += ((row.impresiones || 0) * (row.ctr || 0)) / 100;
            map.set(key, current);
        });

        return Array.from(map.values())
            .map((item) => ({
                ...item,
                cpl: item.leads > 0 ? item.spend / item.leads : null,
                ctr: item.impresiones > 0 ? (item.clicks / item.impresiones) * 100 : null,
                share: totalLeadsMeta > 0 ? (item.leads / totalLeadsMeta) * 100 : 0,
            }))
            .sort((a, b) => (b.leads || 0) - (a.leads || 0));
    }, [countryRows, regionRows, totalLeadsMeta]);

    const bestAge = porEdad[0] || null;
    const lowestCplAge = useMemo(
        () => porEdad.filter((row) => row.leads > 0 && row.cpl != null).sort((a, b) => (a.cpl || 0) - (b.cpl || 0))[0] || null,
        [porEdad]
    );
    const highestCplAge = useMemo(
        () => porEdad.filter((row) => row.leads > 0 && row.cpl != null).sort((a, b) => (b.cpl || 0) - (a.cpl || 0))[0] || null,
        [porEdad]
    );
    const topGeo = geos[0] || null;
    const topGender = porGenero[0] || null;

    const hallazgos = useMemo(() => {
        const items = [];
        if (bestAge) {
            items.push({
                tipo: "oportunidad",
                titulo: `Mayor volumen en ${bestAge.age}`,
                descripcion: `${fmt(bestAge.leads, 0)} leads reportados por Meta y ${fmtPct(bestAge.ctr)} de CTR en el rango con más respuesta.`,
            });
        }
        if (lowestCplAge) {
            items.push({
                tipo: "oportunidad",
                titulo: `CPL más eficiente en ${lowestCplAge.age}`,
                descripcion: `${fmtUsd(lowestCplAge.cpl)} por lead reportado. Conviene priorizar mensajes y creatividades afines a este segmento.`,
            });
        }
        if (highestCplAge && highestCplAge.cpl > (cplPromedio || 0) * 1.3) {
            items.push({
                tipo: "revisar",
                titulo: `Costo alto en ${highestCplAge.age}`,
                descripcion: `Este rango está en ${fmtUsd(highestCplAge.cpl)} de CPL Meta y requiere revisar segmentación o propuesta.`,
            });
        }
        if (topGender) {
            items.push({
                tipo: "revisar",
                titulo: `${topGender.gender} concentra mayor respuesta`,
                descripcion: `${fmt(topGender.leads, 0)} leads Meta (${fmtPct(topGender.share)}) dentro del desglose por género.`,
            });
        }
        if (topGeo) {
            items.push({
                tipo: "oportunidad",
                titulo: `Geografía principal: ${topGeo.nombre}`,
                descripcion: `${fmt(topGeo.leads, 0)} leads Meta y ${fmtPct(topGeo.share)} del total medido provienen de esta ubicación.`,
            });
        }
        if (!items.length) {
            items.push({
                tipo: "revisar",
                titulo: "Sin suficientes hallazgos",
                descripcion: "Meta no devolvió volumen suficiente en breakdowns para generar recomendaciones sólidas.",
            });
        }
        return items.slice(0, 5);
    }, [bestAge, lowestCplAge, highestCplAge, topGender, topGeo, cplPromedio]);

    const ageChart = useMemo(() => ({
        labels: porEdad.map((row) => row.age),
        datasets: [
            {
                label: "Alcance",
                data: porEdad.map((row) => row.alcance || 0),
                backgroundColor: "rgba(124, 58, 237, 0.82)",
                borderRadius: 10,
                maxBarThickness: 36,
            },
            {
                label: "Leads Meta",
                data: porEdad.map((row) => row.leads || 0),
                backgroundColor: "rgba(6, 182, 212, 0.82)",
                borderRadius: 10,
                maxBarThickness: 36,
            },
        ],
    }), [porEdad]);

    const leadsAgeChart = useMemo(() => ({
        labels: porEdad.map((row) => row.age),
        datasets: [
            {
                label: "Leads Meta",
                data: porEdad.map((row) => row.leads || 0),
                backgroundColor: "rgba(154, 197, 0, 0.82)",
                borderRadius: 10,
                maxBarThickness: 44,
            },
        ],
    }), [porEdad]);

    if (cargando) return <ZMLoadingState label="Cargando audiencias..." fullScreen />;
    if (error) return <ZMErrorState message={error} onRetry={cargar} />;

    return (
        <ZMLayout
            title="Audiencias y demográficos"
            subtitle="Cruce entre leads CRM y breakdowns de Meta para entender qué perfiles responden mejor."
            right={datos?.meta?.mensaje || "Actualizado"}
            className={darkMode ? "container-dark" : ""}
        >
            <ZMFiltersBar className="mb-3">
                <div>
                    <label>Desde</label>
                    <input
                        type="date"
                        className="form-control form-control-sm"
                        value={draftFilters.fechaDesde || ""}
                        onChange={(e) => setDraftFilters((prev) => ({ ...prev, fechaDesde: e.target.value }))}
                    />
                </div>
                <div>
                    <label>Hasta</label>
                    <input
                        type="date"
                        className="form-control form-control-sm"
                        value={draftFilters.fechaHasta || ""}
                        onChange={(e) => setDraftFilters((prev) => ({ ...prev, fechaHasta: e.target.value }))}
                    />
                </div>
                <div style={{ minWidth: 320 }}>
                    <label>{"Campa\u00f1a"}</label>
                    <select
                        className="form-select form-select-sm"
                        value={draftFilters.campania || ""}
                        onChange={(e) => setDraftFilters((prev) => ({ ...prev, campania: e.target.value }))}
                    >
                        <option value="">{"Todas las campa\u00f1as"}</option>
                        {campaignOptions.map((option) => (
                            <option key={option.id || option.value} value={option.id || option.value}>
                                {option.name || option.label || option.nombre || option.id}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="zm-filter-actions">
                    <button
                        type="button"
                        className="btn btn-sm zm-btn-primary"
                        onClick={() => {
                            const nextFilters = { ...draftFilters };
                            setFilters(nextFilters);
                            cargar(nextFilters);
                        }}
                    >
                        Aplicar
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => {
                            const reset = defaultFilters();
                            setDraftFilters(reset);
                            setFilters(reset);
                            cargar(reset);
                        }}
                    >
                        Limpiar
                    </button>
                </div>
            </ZMFiltersBar>

            <div className="zm-grid-metrics">
                <ZMMetricCard icon={<Icono.Users />} label="Leads CRM" value={fmt(kpis.leadsCrm, 0)} helper="Fuente principal: LeadsMeta + ficha CRM" empty={!kpis.leadsCrm} />
                <ZMMetricCard icon={<Icono.BarChart />} label="Leads reportados por Meta" value={fmt(kpis.leadsMetaReported ?? totalLeadsMeta, 0)} helper="Breakdowns y acciones de Meta Ads" empty={!kpis.leadsMetaReported && totalLeadsMeta === 0} />
                <ZMMetricCard icon={<Icono.AlertTriangle />} label="Diferencia" value={fmt(kpis.leadsDiferencia, 0)} helper="Meta reportados - Leads CRM" empty={kpis.leadsDiferencia == null} />
                <ZMMetricCard icon={<Icono.CheckCircle />} label="Leads únicos" value={fmt(kpis.leadsUnicos, 0)} helper="Deduplicados por leadgen_id/teléfono/email" empty={!kpis.leadsUnicos} />
                <ZMMetricCard icon={<Icono.Target />} label="Alcance total" value={fmt(totalReach, 0)} empty={totalReach === 0} />
                <ZMMetricCard icon={<Icono.Eye />} label="Impresiones" value={fmt(totalImpressions, 0)} empty={totalImpressions === 0} />
                <ZMMetricCard icon={<Icono.Dollar />} label="CPL Meta reportado" value={fmtUsd(cplPromedio)} empty={cplPromedio == null} />
                <ZMMetricCard icon={<Icono.MousePointer />} label="CTR promedio" value={fmtPct(ctrPromedio)} empty={ctrPromedio == null} />
            </div>

            <ZMCard className="mb-3" bodyClassName="py-2">
                <div className="zm-inline-note">
                    <strong>Lectura correcta:</strong> los breakdowns de audiencias salen de Meta Ads y muestran <b>leads reportados por Meta</b>.
                    Los <b>leads CRM</b> se usan como fuente de verdad del panel y se muestran separados para evitar mezclar atribución con cierre real.
                </div>
            </ZMCard>

            {filas.length === 0 ? (
                <ZMEmptyState
                    title="Sin datos disponibles"
                    hint={datos?.meta?.mensaje || "Meta no devolvió audiencias para este rango o faltan permisos de breakdown."}
                />
            ) : (
                <>
                    <div className="row g-3 mb-3">
                        <div className="col-12 col-xl-4">
                            <ZMCard title="Rendimiento por edad" className="h-100">
                                <ZMTable
                                    rows={porEdad}
                                    emptyText="Sin desglose por edad"
                                    columns={[
                                        { key: "age", header: "Grupo" },
                                        { key: "leads", header: "Leads Meta", cellClassName: "text-end", render: (row) => fmt(row.leads, 0) },
                                        { key: "cpl", header: "CPL Meta", cellClassName: "text-end", render: (row) => fmtUsd(row.cpl) },
                                        { key: "ctr", header: "CTR", cellClassName: "text-end", render: (row) => fmtPct(row.ctr) },
                                    ]}
                                />
                            </ZMCard>
                        </div>
                        <div className="col-12 col-xl-4">
                            <ZMCard title="Alcance por edad" className="h-100">
                                <ZMChartBox className="is-compact">
                                    <Bar
                                        data={ageChart}
                                        options={buildZmChartOptions({
                                            leftTitle: "Volumen",
                                            darkMode,
                                        })}
                                    />
                                </ZMChartBox>
                            </ZMCard>
                        </div>
                        <div className="col-12 col-xl-4">
                            <ZMCard title="Leads Meta por edad" className="h-100">
                                <ZMChartBox className="is-compact">
                                    <Bar
                                        data={leadsAgeChart}
                                        options={buildZmChartOptions({
                                            leftTitle: "Leads Meta",
                                            hideLegend: true,
                                            darkMode,
                                        })}
                                    />
                                </ZMChartBox>
                            </ZMCard>
                        </div>
                    </div>

                    <div className="row g-3 mb-3">
                        <div className="col-12 col-lg-7">
                            <ZMCard title="Distribución por género" className="h-100">
                                <div className="zm-audience-gender-grid">
                                    {porGenero.map((row) => (
                                        <div key={row.gender} className="zm-audience-gender-card">
                                            <div className="d-flex align-items-center gap-2 mb-2">
                                                {row.gender === "Mujeres" ? <Icono.Female /> : row.gender === "Hombres" ? <Icono.Male /> : <Icono.Users />}
                                                <strong>{row.gender}</strong>
                                            </div>
                                            <div className="zm-audience-gender-value">{fmt(row.leads, 0)} leads Meta</div>
                                            <div className="zm-audience-gender-meta">
                                                <span>{fmtPct(row.share)} del total reportado</span>
                                                <span>{fmtUsd(row.cpl)} CPL Meta</span>
                                            </div>
                                            <div className="zm-progress-line">
                                                <div style={{ width: `${Math.max(Math.min(row.share || 0, 100), 4)}%` }} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </ZMCard>
                        </div>
                        <div className="col-12 col-lg-5">
                            <ZMCard title="Hallazgos" className="h-100">
                                <div className="zm-alert-stack">
                                    {hallazgos.map((item, index) => (
                                        <div key={`${item.titulo}-${index}`} className="zm-alert-item">
                                            <div className="d-flex align-items-center justify-content-between gap-2">
                                                <strong>{item.titulo}</strong>
                                                <ZMBadge status={item.tipo} label={item.tipo === "oportunidad" ? "Oportunidad" : "Revisar"} />
                                            </div>
                                            <p>{item.descripcion}</p>
                                        </div>
                                    ))}
                                </div>
                            </ZMCard>
                        </div>
                    </div>

                    <div className="row g-3 mb-3">
                        <div className="col-12 col-lg-7">
                            <ZMCard title="Geografía principal" className="h-100">
                                <ZMTable
                                    rows={geos.slice(0, 8)}
                                    emptyText="Sin detalle geográfico disponible"
                                    columns={[
                                        { key: "nombre", header: countryRows.length > 0 ? "País" : "Región" },
                                        { key: "leads", header: "Leads Meta", cellClassName: "text-end", render: (row) => fmt(row.leads, 0) },
                                        { key: "share", header: "% leads", cellClassName: "text-end", render: (row) => fmtPct(row.share) },
                                        { key: "cpl", header: "CPL Meta", cellClassName: "text-end", render: (row) => fmtUsd(row.cpl) },
                                    ]}
                                />
                            </ZMCard>
                        </div>
                        <div className="col-12 col-lg-5">
                            <ZMCard title="Lectura ejecutiva" className="h-100">
                                <div className="zm-stat-list">
                                    <div className="zm-stat-item">
                                        <div>
                                            <strong>Segmento con más leads</strong>
                                            <span>{bestAge ? bestAge.age : "Sin dato"}</span>
                                        </div>
                                        <b>{bestAge ? fmt(bestAge.leads, 0) : "-"}</b>
                                    </div>
                                    <div className="zm-stat-item">
                                        <div>
                                            <strong>Segmento más eficiente</strong>
                                            <span>{lowestCplAge ? lowestCplAge.age : "Sin dato"}</span>
                                        </div>
                                        <b>{lowestCplAge ? fmtUsd(lowestCplAge.cpl) : "-"}</b>
                                    </div>
                                    <div className="zm-stat-item">
                                        <div>
                                            <strong>Género predominante</strong>
                                            <span>{topGender ? topGender.gender : "Sin dato"}</span>
                                        </div>
                                        <b>{topGender ? fmtPct(topGender.share) : "-"}</b>
                                    </div>
                                    <div className="zm-stat-item">
                                        <div>
                                            <strong>Geo principal</strong>
                                            <span>{topGeo ? topGeo.nombre : "Sin dato"}</span>
                                        </div>
                                        <b>{topGeo ? fmt(topGeo.leads, 0) : "-"}</b>
                                    </div>
                                </div>
                            </ZMCard>
                        </div>
                    </div>

                    <ZMCard title="Detalle de audiencias">
                        <ZMTable
                            rows={filas}
                            emptyText="Sin datos de audiencias"
                            columns={[
                                { key: "breakdown", header: "Breakdown" },
                                { key: "age", header: "Edad", render: (row) => row.age || "-" },
                                { key: "gender", header: "Género", render: (row) => genderLabel(row.gender) },
                                { key: "country", header: "País", render: (row) => row.country || "-" },
                                { key: "region", header: "Región", render: (row) => row.region || "-" },
                                { key: "leadsMetaReported", header: "Leads Meta", cellClassName: "text-end", render: (row) => fmt(row.leadsMetaReported ?? row.leads, 0) },
                                { key: "cpl", header: "CPL Meta", cellClassName: "text-end", render: (row) => fmtUsd(row.cpl) },
                                { key: "alcance", header: "Alcance", cellClassName: "text-end", render: (row) => fmt(row.alcance, 0) },
                                { key: "impresiones", header: "Impresiones", cellClassName: "text-end", render: (row) => fmt(row.impresiones, 0) },
                                { key: "ctr", header: "CTR", cellClassName: "text-end", render: (row) => fmtPct(row.ctr) },
                            ]}
                        />
                    </ZMCard>
                </>
            )}
        </ZMLayout>
    );
}
